import { useState, useRef, useEffect } from 'react';
import { aiApi } from '../api/client';
import { StudyBuddyResponse } from '../types';
import { Send, Bot, User, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import toast from 'react-hot-toast';

interface Message {
  role:    'user' | 'assistant';
  content: string;
  note?:   string;
}

// Browser speech recognition type
interface SpeechRecognition extends EventTarget {
  continuous:       boolean;
  interimResults:   boolean;
  lang:             string;
  start():          void;
  stop():           void;
  onresult:         ((event: any) => void) | null;
  onerror:          ((event: any) => void) | null;
  onend:            (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition:       new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export function StudyBuddyPage() {
  const [messages, setMessages]       = useState<Message[]>([{
    role:    'assistant',
    content: "Hi! I'm EduBuddy 🎓 Ask me anything by typing or using the microphone!",
  }]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [sessionId, setSessionId]     = useState<string | undefined>();
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking]   = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [selectedLang, setSelectedLang] = useState('en-IN');
  const bottomRef                     = useRef<HTMLDivElement>(null);
  const recognitionRef                = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Speech Recognition (Speech → Text) ───────────────────
  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toast.error('Voice input not supported in this browser. Use Chrome.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous     = false;
    recognition.interimResults = false;
    recognition.lang           = selectedLang;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsListening(false);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error('Microphone permission denied. Allow mic access and try again.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    toast('Listening... speak now', { icon: '🎤', duration: 2000 });
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  // ── Text to Speech (Text → Voice) ────────────────────────
  const speak = (text: string) => {
    if (!voiceEnabled) return;
    if (!window.speechSynthesis) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Clean markdown formatting for speech
    const cleanText = text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/#{1,6}\s/g, '')
      .replace(/\n/g, '. ');

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang  = selectedLang;
    utterance.rate  = 0.9;
    utterance.pitch = 1.0;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend   = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // ── Send Message ──────────────────────────────────────────
  const sendMessage = async (messageText?: string) => {
    const text = (messageText || input).trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const res = await aiApi.ask(text, undefined, sessionId);
      const data: StudyBuddyResponse = res.data;

      setSessionId(data.sessionId);
      const aiMessage: Message = {
        role:    'assistant',
        content: data.answer,
        note:    data.note,
      };
      setMessages(prev => [...prev, aiMessage]);

      // Auto-speak the response
      speak(data.answer);

    } catch {
      toast.error('Study Buddy is unavailable right now');
      setMessages(prev => [...prev, {
        role:    'assistant',
        content: 'I\'m having trouble connecting. Please try again.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const languages = [
    { code: 'en-IN', label: '🇮🇳 English' },
    { code: 'hi-IN', label: '🇮🇳 हिंदी' },
    { code: 'en-US', label: '🇺🇸 English US' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 py-8
                    flex flex-col h-[calc(100vh-80px)]">

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2">
            🤖 Study Buddy
          </h1>
          <p className="text-gray-400 text-xs mt-0.5">
            Powered by RAG — answers grounded in your course content
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* Language selector */}
          <select
            value={selectedLang}
            onChange={e => setSelectedLang(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg
                       px-2 py-1 text-gray-300 text-xs
                       focus:outline-none focus:border-green-400"
          >
            {languages.map(l => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>

          {/* Voice toggle */}
          <button
            onClick={() => {
              if (voiceEnabled) stopSpeaking();
              setVoiceEnabled(!voiceEnabled);
            }}
            className={`p-2 rounded-lg border transition-colors
              ${voiceEnabled
                ? 'border-green-400/40 text-green-400 bg-green-400/10'
                : 'border-gray-700 text-gray-500'
              }`}
            title={voiceEnabled ? 'Mute voice' : 'Enable voice'}
          >
            {voiceEnabled
              ? <Volume2 size={14} />
              : <VolumeX size={14} />
            }
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
        {messages.map((msg, i) => (
          <div key={i}
               className={`flex gap-3 ${
                 msg.role === 'user' ? 'justify-end' : 'justify-start'
               }`}>

            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-green-400/20
                              flex items-center justify-center
                              flex-shrink-0 mt-1">
                <Bot size={16} className="text-green-400" />
              </div>
            )}

            <div className={`max-w-[80%] ${
              msg.role === 'user' ? 'order-first' : ''
            }`}>
              <div className={`rounded-2xl px-4 py-3 text-sm
                              leading-relaxed
                ${msg.role === 'user'
                  ? 'bg-green-400 text-gray-950 font-medium rounded-tr-sm'
                  : 'bg-gray-900 border border-gray-800 text-gray-200 rounded-tl-sm'
                }`}>
                {msg.content}
              </div>

              {/* Speak button for AI messages */}
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-2 mt-1 px-1">
                  {msg.note && (
                    <span className="text-xs text-gray-600">
                      {msg.note}
                    </span>
                  )}
                  <button
                    onClick={() => isSpeaking
                      ? stopSpeaking()
                      : speak(msg.content)
                    }
                    className="text-gray-600 hover:text-green-400
                               transition-colors ml-auto"
                    title="Read aloud"
                  >
                    {isSpeaking
                      ? <VolumeX size={12} />
                      : <Volume2 size={12} />
                    }
                  </button>
                </div>
              )}
            </div>

            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gray-800
                              flex items-center justify-center
                              flex-shrink-0 mt-1">
                <User size={16} className="text-gray-400" />
              </div>
            )}
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-green-400/20
                            flex items-center justify-center">
              <Bot size={16} className="text-green-400" />
            </div>
            <div className="bg-gray-900 border border-gray-800
                            rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex gap-1">
                {[0,1,2].map(i => (
                  <div key={i}
                       className="w-2 h-2 bg-green-400 rounded-full
                                  animate-bounce"
                       style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="flex gap-2 items-end">
        <div className="flex-1 relative">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isListening
              ? '🎤 Listening...'
              : 'Ask anything about your lessons...'}
            rows={2}
            className={`w-full bg-gray-900 border rounded-xl
                        px-4 py-3 text-white text-sm
                        focus:outline-none transition-colors resize-none
                        pr-12
              ${isListening
                ? 'border-red-400 bg-red-400/5'
                : 'border-gray-800 focus:border-green-400'
              }`}
          />
        </div>

        {/* Mic button */}
        <button
          onClick={isListening ? stopListening : startListening}
          disabled={loading}
          className={`p-3 rounded-xl transition-all flex-shrink-0
            ${isListening
              ? 'bg-red-400 text-gray-950 animate-pulse'
              : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            } disabled:opacity-50`}
          title={isListening ? 'Stop listening' : 'Speak your question'}
        >
          {isListening ? <MicOff size={18} /> : <Mic size={18} />}
        </button>

        {/* Send button */}
        <button
          onClick={() => sendMessage()}
          disabled={loading || !input.trim()}
          className="p-3 bg-green-400 text-gray-950 rounded-xl
                     hover:bg-green-300 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex-shrink-0"
        >
          <Send size={18} />
        </button>
      </div>

      {/* Voice hint */}
      <p className="text-center text-gray-600 text-xs mt-2">
        {isListening
          ? '🎤 Speak now — tap mic again to stop'
          : '💡 Tap the mic to speak your question in English or Hindi'
        }
      </p>
    </div>
  );
}